/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * UploadDebugPanel — visible diagnostic surface for chunked uploads.
 *
 * Feb 8, 2026 — built in response to a deterministic production loop
 * where the same chunk fails repeatedly without surfacing the actual
 * HTTP status / server detail to the user. This panel renders only
 * when the operator opts in:
 *
 *     localStorage.setItem("lr_debug_uploads", "true");
 *
 * No build-time rebuild needed; flip on the phone, reproduce, paste
 * the on-screen values back to the operator. Useful for both preview
 * and production debugging without changing the user-facing copy.
 *
 * Strict rule: this is read-only. It mounts NOTHING that mutates
 * upload state — it just reflects what the chunkedUpload helper
 * already passed to its onProgress consumer.
 */
import React from "react";

function _readDebugFlag() {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      const v = window.localStorage.getItem("lr_debug_uploads");
      if (v === "true" || v === "1") return true;
    }
  } catch { /* localStorage may throw in private mode */ }
  return process.env.REACT_APP_DEBUG_UPLOADS === "true";
}

export function isDebugEnabled() {
  return _readDebugFlag();
}

function _row(label, value) {
  const display = (value === null || value === undefined || value === "")
    ? "—"
    : String(value);
  return (
    <div className="flex justify-between gap-3 py-0.5 font-mono text-[10.5px]">
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-200 truncate max-w-[60%] text-right">{display}</span>
    </div>
  );
}

export default function UploadDebugPanel({ debug, file, paused }) {
  if (!isDebugEnabled()) return null;

  const d = debug || {};
  // Composite signals — show the user's exact phone story at a glance.
  const fmtBytes = (n) => {
    if (typeof n !== "number") return "—";
    const units = ["B", "KB", "MB", "GB"];
    let v = n; let u = 0;
    while (v >= 1024 && u < units.length - 1) { v /= 1024; u++; }
    return `${v.toFixed(v >= 100 || u === 0 ? 0 : 1)} ${units[u]}`;
  };

  return (
    <div
      data-testid="upload-debug-panel"
      className="mt-3 rounded-lg border border-zinc-700/50 bg-zinc-900/70 p-3 max-w-md mx-auto"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-400">
          Upload diagnostics
        </span>
        <span className="text-[9.5px] text-zinc-500 font-mono">
          lr_debug_uploads=true
        </span>
      </div>
      {_row("upload_id", d.upload_id)}
      {_row("stage", d.stage)}
      {_row("current_chunk", `${d.current_chunk ?? "—"} / ${d.total_chunks ?? "—"}`)}
      {_row("last_successful_chunk", d.last_successful_chunk)}
      {_row("last_failed_chunk", d.last_failed_chunk)}
      {_row("last_http_status", d.last_http_status)}
      {_row("last_error_detail", d.last_error_detail)}
      {_row("retry_count", d.retry_count)}
      {_row("parallelism", d.parallelism)}
      {_row("session_age_seconds", d.session_age_seconds)}
      {_row("mbps", typeof d.mbps === "number" ? d.mbps.toFixed(2) : null)}
      {file && _row("file_name", file.name)}
      {file && _row("file_size", fmtBytes(file.size))}
      {paused && _row("paused_failed_chunk_index", paused.failedChunkIndex)}
      {paused && _row("paused_last_http_status", paused.lastHttpStatus)}
    </div>
  );
}
